import { Component, computed, inject, input, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { filter } from "rxjs";

import { TranslateTokenPipe } from "../../../../../../../core/pipes";
import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { AdaaBoolean, Language } from "../../../../../../constants/enums";
import { MetricsDetailModelType, PropTypeModelType, WorkflowDiffVersionType } from "../../../../../../models";
import { LanguageService, PropertiesService } from "../../../../../../services";
import { FormulaMetricType, MetricType } from "../../_lib/data-entry-diff/types";

// NOTE: the same loop renders no-formula and formula rows, so this is what the
// template reads off a row rather than either metric type on its own
type AuditMetricRowType = {
  date: number;
  year: number;
  month: number;
  quarter: number;
  semester: number;
  frequency: number;
  actual?: number;
  target?: number;
  lowerLimit?: number;
  highLimit?: number;
  ignored?: boolean;
  metrics?: MetricType[];
};

@Component({
  selector: "adaa-audit-data-entry-screen",
  imports: [TranslatePipe, TranslateTokenPipe, RouterLink],
  templateUrl: "./audit-data-entry-screen.html",
  styleUrl: "./audit-data-entry-screen.scss",
})
export class AuditDataEntryScreenComponent {
  readonly languageService = inject(LanguageService);
  private _translateService = inject(TranslateService);
  private _propertiesService = inject(PropertiesService);

  showOnlyNew = input.required<boolean>();
  isAuditTrail = input.required<boolean>();
  newDataentryObject = input<WorkflowDiffVersionType>();
  oldDataentryObject = input<WorkflowDiffVersionType>();
  viewOnlyDifferences = input<boolean>(false);

  months = signal<PropTypeModelType[]>([]);
  trends = signal<PropTypeModelType[]>([]);
  ytpCalcs = signal<PropTypeModelType[]>([]);
  frequencies = signal<PropTypeModelType[]>([]);
  measurementUnits = signal<PropTypeModelType[]>([]);
  title = signal<string>("");

  metricDetails = computed(() => this.newDataentryObject()?.metricDetails as MetricsDetailModelType[]);
  isBounded = computed(() => this.newDataentryObject()?.trend === Constants.TREND.BOUNDED);
  isNoFormula = computed(() => {
    const metricEntities = this.newDataentryObject()?.metricEntities;
    const dataEntryRequests = this.newDataentryObject()?.dataEntryRequests;
    const formula = this.newDataentryObject()?.formula;

    return !metricEntities && !dataEntryRequests && formula === Constants.FORMULA_STRING.NOFORMULA;
  });
  isFormula = computed(() => {
    const dataEntryRequests = this._metrics(this.newDataentryObject(), "dataEntryRequests");
    const hasDetailedTargets = this.newDataentryObject()?.hasTarget === AdaaBoolean.N;

    return !this.isNoFormula() && dataEntryRequests.length > 0 && hasDetailedTargets;
  });
  annualActualReadings = computed(() => this._readings(this.newDataentryObject(), "annualCalculationReadings"));
  newAuditedAnnualActualReadings = computed(() =>
    this._readings(this.newDataentryObject(), "auditedAnnualCalculationReadings")
  );
  oldAuditedAnnualActualReadings = computed(() =>
    this._readings(this.oldDataentryObject(), "auditedAnnualCalculationReadings")
  );
  newDataentryRequests = computed(() => {
    if (this.isNoFormula()) {
      return {
        baselineYear: this.newDataentryObject()?.baselineYear,
        metrics: this._metrics(this.newDataentryObject(), "metrics"),
      };
    }

    return {
      baselineYear: this.newDataentryObject()?.baselineYear,
      metrics: this._metrics(this.newDataentryObject(), "dataEntryRequests"),
    };
  });
  oldDataentryRequests = computed(() => {
    if (this.isNoFormula()) {
      return {
        baselineYear: this.oldDataentryObject()?.baselineYear,
        metrics: this._metrics(this.oldDataentryObject(), "metrics"),
      };
    }

    return {
      baselineYear: this.oldDataentryObject()?.baselineYear,
      metrics: this._metrics(this.oldDataentryObject(), "dataEntryRequests"),
    };
  });
  newMetrics = computed(() => this.newDataentryRequests().metrics);
  oldMetrics = computed(() => this.oldDataentryRequests().metrics);
  metricList = computed(() => {
    const metrics = this.newDataentryRequests().metrics;
    let metricList: FormulaMetricType["metrics"] = [];
    let maxSize = -1;
    for (const metric of metrics) {
      if (!metric.metrics || metric.metrics.length < 1) continue;
      if (metric.metrics.length > maxSize) {
        maxSize = metric.metrics.length;
        metricList = metric.metrics;
      }
    }

    return metricList.reduce<FormulaMetricType["metrics"]>((acc, curr) => {
      const metric = acc?.find(({ kpiMetricId }) => kpiMetricId === curr.kpiMetricId);
      if (!metric) acc?.push(curr);
      return acc;
    }, []);
  });
  frequency = computed(() => {
    if (AdaaHelper.isDefined(this.newDataentryObject()?.frequency)) {
      return this.newDataentryObject()?.frequency;
    }
    return 0;
  });
  indexesToShow = computed(() => {
    const seen = new Set<number>();
    const affected: AuditMetricRowType[] = [];

    for (const metric of this.newMetrics()) {
      if (seen.has(metric.year)) continue;
      seen.add(metric.year);
      affected.push(metric);
    }

    return affected;
  });

  readonly isDefinedAndNotEmpty = AdaaHelper.isDefinedAndNotEmpty.bind(AdaaHelper);
  readonly spanRows = (year: number) => {
    const num = this.newMetrics().filter((m) => m.year === year);
    return num.length;
  };
  readonly shouldShowRow = (date: number) => {
    const indexesToShow = this.indexesToShow();
    const found = indexesToShow.find((m) => m.date === date);
    return !!found;
  };

  public ngOnInit() {
    this._fetchConstants();
  }

  private _metrics(object: WorkflowDiffVersionType | undefined, field: "metrics" | "dataEntryRequests") {
    return (object?.[field] ?? []) as AuditMetricRowType[];
  }

  private _readings(
    object: WorkflowDiffVersionType | undefined,
    field: "annualCalculationReadings" | "auditedAnnualCalculationReadings"
  ) {
    return (object?.[field] ?? []) as Record<string, unknown>[];
  }

  private _fetchConstants() {
    const constantIds = [
      Constants.CONSTANT_MEASUREMENT_UNIT,
      Constants.CONSTANT_FREQUENCY,
      Constants.CONSTANT_TREND,
      Constants.CONSTANT_YTP,
      Constants.CONSTANT_MONTHS,
    ];

    this._propertiesService
      .getPropByIdList(constantIds)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => {
          this.ytpCalcs.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_YTP));
          this.trends.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_TREND));
          this.months.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_MONTHS));
          this.frequencies.set(res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_FREQUENCY));
          this.measurementUnits.set(
            res.responseData.filter(({ propType }) => propType === Constants.CONSTANT_MEASUREMENT_UNIT)
          );
        },
      });
  }

  public getKpitypeName(kpiType?: number) {
    if (kpiType == null) return undefined;
    return {
      [Constants.KPI_TYPE.NKPI]: "nkpi",
      [Constants.KPI_TYPE.SKPI]: "skpi",
      [Constants.KPI_TYPE.MOKPI]: "mokpi",
      [Constants.KPI_TYPE.IPTKPI]: "iptkpi",
      [Constants.KPI_TYPE.MTKPI]: "mtkpi",
      [Constants.KPI_TYPE.NTKPI]: "ntkpi",
      [Constants.KPI_TYPE.DTKPI]: "dtkpi",
      [Constants.KPI_TYPE.OPM]: "opm",
      [Constants.KPI_TYPE.UAE]: "uae",
      [Constants.KPI_TYPE.EKPI]: "ekpi",
      [Constants.KPI_TYPE.SRVKPI]: "srvkpi",
      [Constants.KPI_TYPE.IMKPI]: "imkpi",
      [Constants.KPI_TYPE.AIKPI]: "aikpi",
    }[kpiType];
  }

  public getAnnualActual(year: number) {
    const reading = this.annualActualReadings().find((item) => item.year === year);
    if (!reading) return;
    if (reading.ignored) return "N/A";
    return reading.actual;
  }

  public showDiff(year: number, item: "comment" | "auditedActual") {
    const newData = this.getAuditedAnnualReading(year, { item, type: "new" });
    const oldData = this.getAuditedAnnualReading(year, { item, type: "old" });

    return newData !== oldData;
  }

  public getAuditedAnnualReading(year: number, opts: { type: "new" | "old"; item: "comment" | "auditedActual" }) {
    let data: Record<string, unknown>[];
    if (opts.type === "new") {
      data = this.newAuditedAnnualActualReadings();
    } else {
      data = this.oldAuditedAnnualActualReadings();
    }
    const reading = data.find((item) => item.year === year);
    if (!reading) return;
    if (opts.item === "auditedActual") {
      if (reading.ignored) return "N/A";
      return reading.auditedActual;
    }
    return reading.comment ?? "";
  }

  public getNoFormulaPeriodLabel(metric: AuditMetricRowType) {
    if (metric.frequency === Constants.FREQUENCY.MONTHLY) {
      return `${AdaaHelper.getMonthName(metric.month, AdaaHelper.getCurrentLang(), false)} ${metric.year}`;
    }

    if (this.frequency() === Constants.FREQUENCY.QUARTERLY) {
      switch (this.languageService.current()) {
        case Language.English:
          return `${this._translateService.instant(`targets.periods.quarters.${metric.quarter}`)} ${metric.year}`;
        default:
          return `${this._translateService.instant("targets.quarter")} ${metric.quarter} ${metric.year}`;
      }
    }

    if (this.frequency() === Constants.FREQUENCY.SEMIANNUAL) {
      switch (this.languageService.current()) {
        case Language.Arabic:
          return `${this._translateService.instant(`targets.periods.semestral.${metric.semester}`)} ${metric.year}`;
        default:
          return `${this._translateService.instant("targets.semiannual")} ${metric.semester} ${metric.year}`;
      }
    }

    return metric.year;
  }

  public isBaselineMetric(year: number) {
    const metrics = (this.newDataentryObject()?.metrics ?? []) as { year: number; isBaselineTarget?: boolean }[];
    return metrics.find((e) => e.year === year)?.isBaselineTarget;
  }

  public formatFromConstants(key: "measurement" | "freq" | "ytp" | "trend" | "month", id?: number) {
    switch (key) {
      case "freq": {
        const freq = this.frequencies().find((e) => e.id === id);
        return freq ? AdaaHelper.getItemValueByToken(freq, "name") : "";
      }

      case "measurement": {
        const measurement = this.measurementUnits().find((e) => e.id === id);
        return measurement ? AdaaHelper.getItemValueByToken(measurement, "name") : "";
      }

      case "ytp": {
        const ytp = this.ytpCalcs().find((e) => e.id === id);
        return ytp ? AdaaHelper.getItemValueByToken(ytp, "name") : "";
      }

      case "trend": {
        const trend = this.trends().find((e) => e.id === id);
        return trend ? AdaaHelper.getItemValueByToken(trend, "name") : "";
      }

      case "month": {
        const month = this.months().find((e) => e.id === id);
        return month ? AdaaHelper.getItemValueByToken(month, "name") : "";
      }
    }
  }

  public getFormula(formula?: string) {
    if (formula === Constants.FORMULA_STRING.NOFORMULA) return "skpi.no_formula";
    return formula;
  }

  public getInfo(metricType: string) {
    let title = "metrics.global";
    if (metricType === Constants.METRICS_TYPE.LOCAL) title = "metrics.local";
    else if (metricType === Constants.METRICS_TYPE.DIMENSION) title = "main_types.D";

    this.title.set(this._translateService.instant(title));
  }
}
